import { Head, router } from "@inertiajs/react"
import { format } from "date-fns"

import { FilterChip, FilterRow } from "@/components/admin/filter-chip"
import { PageHeader } from "@/components/console/page-header"
import { type StatusTier } from "@/components/ui/badge"
import { Table, TableCard } from "@/components/ui/table"
import { TabList, Tabs } from "@/components/ui/tabs"
import { AdminLayout } from "@/layouts/admin-layout"
import { adminCommitmentsPath, adminSchoolPath } from "@/routes"

import type { CommitmentRow, CommitmentState } from "./types"

interface Props {
  commitments: CommitmentRow[]
  states: { key: CommitmentState; label: string; count: number }[]
  scopes: { id: number; label: string }[]
  filters: { scope_id: number | null; state: CommitmentState }
}

const day = (iso: string) => format(new Date(iso), "d MMM yyyy")

const STATE_COLORS: Record<CommitmentState, StatusTier> = {
  open: "remote",
  matched: "confirmed",
  lapsed: "system",
}

/**
 * The Team Leader's forward view: every verbal booking, oldest first, with who
 * took it, how long it has waited for a job, and the last attempt on the school.
 * Read-only; the nightly matcher closes a commitment when its job appears.
 */
export default function AdminCommitmentsIndex({
  commitments,
  states,
  scopes,
  filters,
}: Props) {
  const visit = (next: Partial<Props["filters"]>) => {
    const merged = { ...filters, ...next }
    router.visit(adminCommitmentsPath(), {
      data: {
        ...(merged.scope_id ? { scope_id: merged.scope_id } : {}),
        ...(merged.state !== "open" ? { state: merged.state } : {}),
      },
      preserveState: true,
      preserveScroll: true,
      only: ["commitments", "states", "filters"],
    })
  }

  const stateLabel =
    states.find((s) => s.key === filters.state)?.label ?? filters.state
  const scope = scopes.find((s) => s.id === filters.scope_id)
  const tokens = scope
    ? [
        {
          label: `scope: ${scope.label.toLowerCase()}`,
          onClear: () => visit({ scope_id: null }),
        },
      ]
    : []

  return (
    <AdminLayout>
      <Head title="Commitments" />
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[{ label: "call list" }, { label: "commitments" }]}
          title="Commitments"
          description="Verbal bookings from logged calls. One closes when its job appears in Salesforce, and lapses when the season it was for closes without one."
        />

        {scopes.length > 0 && (
          <FilterRow label="Scope">
            <FilterChip
              active={!filters.scope_id}
              onClick={() => visit({ scope_id: null })}
            >
              All
            </FilterChip>
            {scopes.map((s) => (
              <FilterChip
                key={s.id}
                active={filters.scope_id === s.id}
                onClick={() => visit({ scope_id: s.id })}
              >
                {s.label}
              </FilterChip>
            ))}
          </FilterRow>
        )}

        <Tabs
          selectedKey={filters.state}
          onSelectionChange={(k) =>
            visit({ state: String(k) as CommitmentState })
          }
        >
          <TabList
            size="sm"
            type="underline"
            items={states.map((s) => ({
              id: s.key,
              label: s.label,
              badge: s.count,
            }))}
          />
        </Tabs>

        <TableCard.Root bleed>
          <TableCard.Caption
            title={stateLabel}
            count={`${commitments.length} ${commitments.length === 1 ? "commitment" : "commitments"}`}
            tokens={tokens}
            note="booked ↑"
          />
          {commitments.length === 0 ? (
            <TableCard.Empty>
              A booked-verbal call from a school card opens one.
            </TableCard.Empty>
          ) : (
            <>
              <Table aria-label="Commitments">
                <Table.Header>
                  <Table.Head
                    id="school"
                    isRowHeader
                    label="School"
                    className="w-full"
                  />
                  <Table.Head id="cohort" label="Cohort" />
                  <Table.Head id="coordinator" label="Coordinator" />
                  <Table.Head
                    id="made"
                    label="Booked"
                    align="right"
                    sorted="asc"
                  />
                  <Table.Head id="open" label="Days open" align="right" />
                  <Table.Head id="last" label="Last attempt" />
                  <Table.Head id="state" label="State" />
                </Table.Header>
                <Table.Body items={commitments}>
                  {(row) => (
                    <Table.Row
                      id={row.id}
                      href={
                        row.school_id
                          ? adminSchoolPath(row.school_id)
                          : undefined
                      }
                    >
                      <Table.Lead>{row.school_name}</Table.Lead>
                      <Table.Cell className="text-secondary">
                        {row.cohort}
                        {row.expected_term ? ` · ${row.expected_term}` : ""}
                      </Table.Cell>
                      <Table.Cell>{row.coordinator ?? "—"}</Table.Cell>
                      <Table.When at={row.made_on} pattern="d MMM yyyy" />
                      <Table.Figure>{row.days_open}</Table.Figure>
                      <Table.Cell>
                        {row.last_attempt
                          ? `${row.last_attempt.outcome_label}, ${day(row.last_attempt.on)}`
                          : "—"}
                      </Table.Cell>
                      <Table.Status tier={STATE_COLORS[row.state]}>
                        {row.state === "matched" && row.matched_job_name
                          ? row.matched_job_name
                          : row.state}
                      </Table.Status>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
              <TableCard.Footer total={commitments.length} />
            </>
          )}
        </TableCard.Root>
      </div>
    </AdminLayout>
  )
}
